import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "../api";
import { loadPublicDomains, guestBase } from "../publicDomains";
import { LOGO_URL } from "../brand";
import WelcomeCenterManager from "../components/welcome/WelcomeCenterManager";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  QrCode, Plus, Copy, MessageCircle, Mail, Printer, Pencil, Trash2, ExternalLink,
  ScanLine, Users, TrendingUp, Euro, GripVertical, BookOpen, ShoppingBag,
  Image as ImageIcon, Inbox, Link2, RefreshCw, CheckCircle2, Loader2, ConciergeBell, Languages, ChevronDown, Save,
} from "lucide-react";

// Base dominio ospite brandizzato (fallback dominio corrente finché il DNS non è attivo)
let _guestBase = window.location.origin;
const guestUrl = (code) => `${_guestBase}/g/${code}`;
const checkinUrl = (code) => `${_guestBase}/checkin/${code}`;

export default function QRCenter() {
  const [apts, setApts] = useState([]);
  const [aptId, setAptId] = useState("");
  const [tab, setTab] = useState("codes");

  useEffect(() => {
    loadApts();
    // eslint-disable-next-line
  }, []);
  const loadApts = () => api.get("/apartments/lookup").then((r) => {
    setApts(r.data);
    if (r.data.length && !aptId) setAptId(r.data[0].id);
  }).catch(() => toast.error("Errore caricamento immobili"));

  const aptName = apts.find((a) => a.id === aptId)?.name || "";

  return (
    <div className="space-y-6" data-testid="qr-center">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-indigo-600"><QrCode size={16} /> QR Center Intelligente</div>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            QR dinamici per appartamento: la destinazione è modificabile in qualsiasi momento senza ristampare il codice.
            La pagina ospite (Welcome Guide) è pubblica e ottimizzata per mobile.
          </p>
        </div>
        <div className="w-full sm:w-72">
          <Select value={aptId} onValueChange={setAptId}>
            <SelectTrigger data-testid="qr-apartment-select"><SelectValue placeholder="Seleziona immobile" /></SelectTrigger>
            <SelectContent>
              {apts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code ? `${a.code} · ` : ""}{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {aptId && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="qr-tabs">
            <TabsTrigger value="codes" data-testid="tab-codes">QR Codes</TabsTrigger>
            <TabsTrigger value="links" data-testid="tab-links">Link Pubblici</TabsTrigger>
            <TabsTrigger value="guide" data-testid="tab-guide">Welcome Guide</TabsTrigger>
            <TabsTrigger value="welcome" data-testid="tab-welcome">Welcome Center</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">Servizi extra</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests">Richieste</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          </TabsList>
          <TabsContent value="codes" className="mt-5"><QrCodesTab aptId={aptId} aptName={aptName} /></TabsContent>
          <TabsContent value="links" className="mt-5"><PublicLinksTab apts={apts} reload={loadApts} /></TabsContent>
          <TabsContent value="guide" className="mt-5"><GuideTab aptId={aptId} /></TabsContent>
          <TabsContent value="welcome" className="mt-5"><WelcomeCenterManager aptId={aptId} /></TabsContent>
          <TabsContent value="services" className="mt-5"><UpsellTab aptId={aptId} /></TabsContent>
          <TabsContent value="requests" className="mt-5"><RequestsTab aptId={aptId} /></TabsContent>
          <TabsContent value="analytics" className="mt-5"><AnalyticsTab aptId={aptId} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

/* ---------------- Link Pubblici ---------------- */
function PublicLinksTab({ apts, reload }) {
  const [qr, setQr] = useState(null); // {title, url}
  const [open, setOpen] = useState({}); // apartment advanced expanded
  const [links, setLinks] = useState({}); // aptId -> {n: url} (override facoltativi)
  const [saving, setSaving] = useState("");
  const [, setDomTick] = useState(0);
  useEffect(() => { loadPublicDomains().then((d) => { _guestBase = guestBase(d); setDomTick((t) => t + 1); }); }, []);

  useEffect(() => {
    const init = {};
    apts.forEach((a) => { init[a.id] = { ...(a.checkin_links || {}) }; });
    setLinks(init);
  }, [apts]);

  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("Link copiato"); };
  const printQr = () => {
    const canvas = document.querySelector("#public-qr-canvas canvas");
    const dataUrl = canvas ? canvas.toDataURL("image/png") : "";
    const w = window.open("", "_blank");
    w.document.write(`<!doctype html><html><head><title>QR Check-in · ${qr?.title || ""}</title></head>
      <body style="text-align:center;font-family:sans-serif;padding:40px">
      <img src="${LOGO_URL}" style="height:60px"/><h2>${qr?.title || ""}</h2>
      <img src="${dataUrl}" style="width:280px"/><p style="color:#64748b;font-size:12px">${qr?.url || ""}</p></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 400);
  };
  const setSlot = (aid, n, val) => setLinks((l) => ({ ...l, [aid]: { ...l[aid], [n]: val } }));
  const overrideCount = (a) => Object.values(links[a.id] || {}).filter((v) => (v || "").trim()).length;
  const save = async (aid) => {
    setSaving(aid);
    try {
      await api.put(`/apartments/${aid}/checkin-links`, { checkin_links: links[aid] || {} });
      toast.success("Override check-in salvati");
      reload?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(""); }
  };

  const withCode = apts.filter((a) => a.code);
  return (
    <div data-testid="public-links-tab">
      <p className="mb-4 max-w-3xl text-sm text-slate-500">
        Ogni appartamento ha <b>un unico link pubblico di check-in</b>: <b className="font-mono">/checkin/&lt;codice&gt;</b>.
        Aprendolo, l'ospite sceglie il numero di ospiti e viene indirizzato al modulo Tally corretto (mappa globale gestita nelle <b>Impostazioni</b>).
        Gli <b>override Tally per appartamento</b> sono facoltativi e servono solo per casi particolari (sezione avanzata).
      </p>
      <div className="space-y-3">
        {withCode.map((a) => {
          const max = a.max_guests || 1;
          const pageUrl = checkinUrl(a.code);
          const isOpen = open[a.id];
          const ovc = overrideCount(a);
          return (
            <Card key={a.id} data-testid={`public-link-row-${a.id}`} className="overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">{a.name}</p>
                  <p className="text-xs text-slate-400">{a.code} · <span className="inline-flex items-center gap-1"><Users size={11} /> {max} ospiti</span></p>
                  <p className="mt-1 truncate font-mono text-xs text-indigo-600">{pageUrl}</p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" data-testid={`copy-link-${a.id}`} onClick={() => copy(pageUrl)}><Copy size={12} /> Copia</Button>
                  <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" data-testid={`open-link-${a.id}`} onClick={() => window.open(pageUrl, "_blank")}><ExternalLink size={12} /> Apri</Button>
                  <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" data-testid={`qr-link-${a.id}`} onClick={() => setQr({ title: a.name, url: pageUrl })}><QrCode size={12} /> QR</Button>
                </div>
              </div>
              <div className="border-t border-slate-100 px-4 py-2">
                <button className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800"
                  data-testid={`toggle-advanced-${a.id}`} onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}>
                  <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  Override Tally avanzati {ovc > 0 && <Badge className="ml-1 bg-indigo-100 text-indigo-700">{ovc} attivi</Badge>}
                </button>
              </div>
              {isOpen && (
                <div className="border-t border-slate-100 bg-slate-50/60 p-4">
                  <p className="mb-3 text-xs text-slate-500">
                    Lascia vuoto per usare la <b>mappa globale</b> (Impostazioni). Compila solo per sovrascrivere un link Tally specifico per questo appartamento e numero di ospiti (HTTPS).
                  </p>
                  <div className="space-y-2">
                    {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
                      const url = (links[a.id] || {})[n] || "";
                      return (
                        <div key={n} className="flex flex-wrap items-center gap-2" data-testid={`checkin-slot-${a.id}-${n}`}>
                          <span className="w-28 shrink-0 text-sm text-slate-600">{n} {n === 1 ? "ospite" : "ospiti"}</span>
                          <Input className="h-9 flex-1 font-mono text-xs" placeholder="(usa mappa globale)" value={url}
                            data-testid={`checkin-input-${a.id}-${n}`} onChange={(e) => setSlot(a.id, n, e.target.value)} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button size="sm" className="gap-1.5" data-testid={`save-links-${a.id}`} disabled={saving === a.id} onClick={() => save(a.id)}>
                      <Save size={14} /> {saving === a.id ? "Salvataggio…" : "Salva override"}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
        {withCode.length === 0 && <p className="p-8 text-center text-slate-400">Nessun appartamento con codice. Assegna un codice all'immobile per generare i link.</p>}
      </div>

      <Dialog open={!!qr} onOpenChange={(o) => !o && setQr(null)}>
        <DialogContent data-testid="public-qr-dialog">
          <DialogHeader>
            <DialogTitle>QR Check-in · {qr?.title}</DialogTitle>
            <DialogDescription>QR del link di check-in. Stampalo o mostralo all'ospite.</DialogDescription>
          </DialogHeader>
          {qr && (
            <div className="flex flex-col items-center gap-3 py-2" id="public-qr-canvas">
              <QRCodeCanvas value={qr.url} size={220} includeMargin level="M" />
              <p className="break-all text-center font-mono text-xs text-slate-500">{qr.url}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => copy(qr?.url)}><Copy size={14} className="mr-1.5" /> Copia link</Button>
            <Button onClick={printQr}><Printer size={14} className="mr-1.5" /> Stampa QR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- QR Codes ---------------- */
function QrCodesTab({ aptId, aptName }) {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ title: "", type: "welcome_guide", target_type: "guide", target_url: "" });
  const [, setDomTick] = useState(0);
  useEffect(() => { loadPublicDomains().then((d) => { _guestBase = guestBase(d); setDomTick((t) => t + 1); }); }, []);

  const load = useCallback(() => {
    api.get(`/qr-codes?apartment_id=${aptId}`).then((r) => setItems(r.data)).catch(() => {});
  }, [aptId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/qr-codes", { apartment_id: aptId, ...form });
      toast.success("QR creato");
      setCreating(false);
      setForm({ title: "", type: "welcome_guide", target_type: "guide", target_url: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/qr-codes/${edit.id}`, {
        title: edit.title, target_type: edit.target_type, target_url: edit.target_url, enabled: edit.enabled,
      });
      toast.success("QR aggiornato");
      setEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const toggle = async (qr) => {
    await api.put(`/qr-codes/${qr.id}`, { enabled: !qr.enabled });
    load();
  };

  const del = async (qr) => {
    if (!window.confirm("Eliminare questo QR? I dati di scansione verranno rimossi.")) return;
    await api.delete(`/qr-codes/${qr.id}`);
    toast.success("QR eliminato");
    load();
  };

  const copyLink = (qr) => { navigator.clipboard?.writeText(guestUrl(qr.code)); toast.success("Link copiato"); };
  const waLink = (qr) => `https://wa.me/?text=${encodeURIComponent(`Ecco la tua Welcome Guide: ${guestUrl(qr.code)}`)}`;
  const mailLink = (qr) => `mailto:?subject=${encodeURIComponent("La tua Welcome Guide")}&body=${encodeURIComponent(`Buongiorno,\n\necco il link alla Welcome Guide:\n${guestUrl(qr.code)}\n\nUrban Dreams Home`)}`;

  const printQr = (qr) => {
    const canvas = document.getElementById(`qr-canvas-${qr.id}`);
    const dataUrl = canvas ? canvas.toDataURL("image/png") : "";
    const w = window.open("", "_blank", "width=600,height=800");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>QR · ${aptName}</title>
      <style>body{font-family:Georgia,serif;text-align:center;padding:40px;color:#0f172a}
      img.logo{max-height:70px;margin-bottom:16px}.title{font-size:22px;font-weight:600;margin:8px 0}
      .label{font-size:15px;color:#475569;margin-bottom:20px}.qr{margin:20px auto}
      .hint{font-size:13px;color:#64748b;margin-top:16px}.code{font-family:monospace;color:#94a3b8;font-size:12px;margin-top:8px}</style>
      </head><body>
      <img class="logo" src="${LOGO_URL}"/><div class="title">${aptName}</div>
      <div class="label">${qr.design?.label || qr.title || "Welcome Guide"}</div>
      <img class="qr" src="${dataUrl}" width="260" height="260"/>
      <div class="hint">Inquadra il QR con la fotocamera per aprire la guida</div>
      <div class="code">${guestUrl(qr.code)}</div>
      </body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 300);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="qr-new-btn" className="gap-2" onClick={() => setCreating(true)}><Plus size={16} /> Nuovo QR</Button>
      </div>

      {items.length === 0 && <p className="py-10 text-center text-sm text-slate-400" data-testid="qr-empty">Nessun QR per questo immobile. Creane uno.</p>}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.map((qr) => (
          <Card key={qr.id} data-testid={`qr-card-${qr.code}`} className="flex flex-col items-center border-slate-200 p-5 text-center">
            <div className="rounded-xl bg-white p-3 ring-1 ring-slate-100">
              <QRCodeCanvas id={`qr-canvas-${qr.id}`} value={guestUrl(qr.code)} size={140}
                fgColor={qr.design?.color || "#0f172a"} level="M" includeMargin />
            </div>
            <h3 className="mt-3 font-medium text-slate-900">{qr.title}</h3>
            <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
              <Badge variant={qr.enabled ? "default" : "secondary"}>{qr.enabled ? "Attivo" : "Disattivo"}</Badge>
              <span className="inline-flex items-center gap-1"><ScanLine size={12} /> {qr.scan_count} scansioni</span>
            </div>
            <div className="mt-1 text-[11px] text-slate-400">
              {qr.target_type === "url" ? "→ URL esterno" : "→ Welcome Guide"} · /g/{qr.code}
            </div>

            <Separator className="my-3" />
            <div className="flex flex-wrap justify-center gap-1.5">
              <Button size="sm" variant="outline" className="gap-1" onClick={() => copyLink(qr)} data-testid={`qr-copy-${qr.code}`}><Copy size={13} /> Link</Button>
              <a href={waLink(qr)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="gap-1"><MessageCircle size={13} /> WA</Button></a>
              <a href={mailLink(qr)}><Button size="sm" variant="outline" className="gap-1"><Mail size={13} /> Email</Button></a>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => printQr(qr)} data-testid={`qr-print-${qr.code}`}><Printer size={13} /> Stampa</Button>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Switch checked={qr.enabled} onCheckedChange={() => toggle(qr)} data-testid={`qr-toggle-${qr.code}`} />
              <Button size="sm" variant="ghost" className="gap-1" onClick={() => setEdit({ ...qr })} data-testid={`qr-edit-${qr.code}`}><Pencil size={13} /></Button>
              <Button size="sm" variant="ghost" className="gap-1 text-red-600" onClick={() => del(qr)} data-testid={`qr-delete-${qr.code}`}><Trash2 size={13} /></Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="qr-create-dialog">
          <DialogHeader>
            <DialogTitle>Nuovo QR dinamico</DialogTitle>
            <DialogDescription>Il codice è permanente; la destinazione è modificabile in seguito senza ristampare.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Titolo / etichetta</label>
              <Input data-testid="qr-form-title" value={form.title} placeholder="Es. Welcome Guide ingresso"
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Destinazione</label>
              <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v })}>
                <SelectTrigger data-testid="qr-form-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="guide">Welcome Guide (pagina ospite)</SelectItem>
                  <SelectItem value="url">URL esterno</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.target_type === "url" && (
              <Input data-testid="qr-form-url" value={form.target_url} placeholder="https://…"
                onChange={(e) => setForm({ ...form, target_url: e.target.value, type: "custom" })} />
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Annulla</Button>
            <Button data-testid="qr-create-submit" onClick={create}>Crea QR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="qr-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifica destinazione</DialogTitle>
            <DialogDescription>Cambia dove punta il QR senza ristampare il codice fisico.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} placeholder="Titolo" />
              <Select value={edit.target_type} onValueChange={(v) => setEdit({ ...edit, target_type: v })}>
                <SelectTrigger data-testid="qr-edit-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="guide">Welcome Guide (pagina ospite)</SelectItem>
                  <SelectItem value="url">URL esterno</SelectItem>
                </SelectContent>
              </Select>
              {edit.target_type === "url" && (
                <Input value={edit.target_url} placeholder="https://…" onChange={(e) => setEdit({ ...edit, target_url: e.target.value })} />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Annulla</Button>
            <Button data-testid="qr-edit-submit" onClick={saveEdit}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Welcome Guide ---------------- */
function GuideTab({ aptId }) {
  const [guide, setGuide] = useState(null);
  const [elang, setElang] = useState("it");
  const [tstatus, setTstatus] = useState([]);
  const [translating, setTranslating] = useState(false);
  const [catalog, setCatalog] = useState([]);

  const load = useCallback(() => {
    api.get(`/welcome-guides/${aptId}`).then((r) => setGuide(r.data)).catch(() => {});
    api.get(`/welcome-guides/${aptId}/translation-status`).then((r) => setTstatus(r.data.languages)).catch(() => {});
  }, [aptId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/service-requests/meta").then((r) => setCatalog(r.data.catalog || [])).catch(() => {}); }, []);

  const set = (k, v) => setGuide((g) => ({ ...g, [k]: v }));
  const setSection = (i, k, v) => setGuide((g) => {
    const s = [...g.sections]; s[i] = { ...s[i], [k]: v }; return { ...g, sections: s };
  });
  // translations per lingua (id stabile). IT = campi base.
  const [tfilter, setTfilter] = useState("all"); // all | missing | toverify
  const tval = (id) => (guide.translations?.[elang]?.[id]) ?? "";
  const tset = (id, v) => setGuide((g) => {
    const tr = { ...(g.translations || {}) }; tr[elang] = { ...(tr[elang] || {}), [id]: v };
    const ts = { ...(g.translations_status || {}) }; ts[elang] = { ...(ts[elang] || {}), [id]: "verified" };
    return { ...g, translations: tr, translations_status: ts };
  });
  const approve = (id) => setGuide((g) => {
    const ts = { ...(g.translations_status || {}) }; ts[elang] = { ...(ts[elang] || {}), [id]: "verified" };
    return { ...g, translations_status: ts };
  });
  const fstate = (id, lang = elang) => {
    if (lang === "it") return "green";
    const val = guide.translations?.[lang]?.[id];
    if (!val || !String(val).trim()) return "red";
    return guide.translations_status?.[lang]?.[id] === "verified" ? "green" : "yellow";
  };
  const surfaceIds = () => {
    const ids = [];
    if ((guide.hero_title || "").trim()) ids.push("field:hero_title");
    if ((guide.hero_subtitle || "").trim()) ids.push("field:hero_subtitle");
    if ((guide.intro || "").trim()) ids.push("field:intro");
    (guide.sections || []).forEach((s) => { if (s.visible !== false) { if ((s.title || "").trim()) ids.push(`section_title:${s.id}`); if ((s.body || "").trim()) ids.push(`section_body:${s.id}`); } });
    (catalog || []).forEach((c) => ids.push(`service:${c.key}`));
    return ids;
  };
  const countFor = (lang) => {
    const ids = surfaceIds(); const total = ids.length || 1;
    if (lang === "it") return { green: ids.length, yellow: 0, red: 0, total: ids.length, percent: 100 };
    let g = 0, y = 0, r = 0;
    ids.forEach((id) => { const st = fstate(id, lang); if (st === "green") g++; else if (st === "yellow") y++; else r++; });
    return { green: g, yellow: y, red: r, total: ids.length, percent: Math.round(100 * (g + y) / total) };
  };
  const showField = () => true;
  const StatusRow = ({ label }) => <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>;
  const addSection = () => setGuide((g) => ({
    ...g, sections: [...(g.sections || []), { id: `new-${Date.now()}`, key: "custom", title: "Nuova sezione", body: "", order: (g.sections?.length || 0), visible: true }],
  }));
  const removeSection = (i) => setGuide((g) => ({ ...g, sections: g.sections.filter((_, idx) => idx !== i) }));

  const save = async () => {
    try {
      const payload = { ...guide };
      payload.sections = payload.sections.map((s, i) => ({ ...s, order: i }));
      const r = await api.put(`/welcome-guides/${aptId}`, payload);
      setGuide(r.data);
      api.get(`/welcome-guides/${aptId}/translation-status`).then((x) => setTstatus(x.data.languages)).catch(() => {});
      toast.success("Welcome Guide salvata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const translateWithIvy = async () => {
    try {
      setTranslating(true);
      const payload = { ...guide }; payload.sections = payload.sections.map((s, i) => ({ ...s, order: i }));
      await api.put(`/welcome-guides/${aptId}`, payload); // salva prima i testi IT
      const r = await api.post(`/welcome-guides/${aptId}/translate`, {});
      setTstatus(r.data.languages);
      const g = await api.get(`/welcome-guides/${aptId}`); setGuide(g.data);
      toast.success("Traduzioni generate con Ivy 🌍");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore traduzione"); }
    finally { setTranslating(false); }
  };

  const [uploading, setUploading] = useState(false);
  const uploadGallery = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/welcome-guides/${aptId}/gallery`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGuide((g) => ({ ...g, gallery: [...(g.gallery || []), r.data] }));
      toast.success("Immagine aggiunta alla gallery");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removeGalleryImg = async (imgId) => {
    try {
      await api.delete(`/welcome-guides/${aptId}/gallery/${imgId}`);
      setGuide((g) => ({ ...g, gallery: (g.gallery || []).filter((x) => x.id !== imgId) }));
    } catch (e) { toast.error("Errore"); }
  };

  if (!guide) return <p className="py-10 text-center text-sm text-slate-400">Caricamento…</p>;
  const isIt = elang === "it";

  return (
    <div className="space-y-5" data-testid="guide-editor">
      <Card className="border-slate-200 bg-slate-50/60 p-5" data-testid="guide-translations">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Languages size={16} /> Lingua dei contenuti</div>
        <p className="mb-3 text-xs text-slate-500">Compila i testi della guida in ogni lingua e premi "Salva Welcome Guide". Se un testo manca in una lingua, l'ospite vedrà automaticamente la versione inglese.</p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Stai modificando:</span>
          {[["it", "🇮🇹 Italiano"], ["en", "🇬🇧 English"], ["es", "🇪🇸 Español"]].map(([l, lab]) => (
            <button key={l} onClick={() => setElang(l)} data-testid={`guide-edit-lang-${l}`}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${elang === l ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"}`}>{lab}</button>
          ))}
        </div>
      </Card>

      <Card className="border-slate-200 p-5">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><BookOpen size={16} /> Contenuto guida {!isIt && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs uppercase text-slate-500">{elang}</span>}</div>
        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          {showField("field:hero_title") && (
            <div>
              <StatusRow id="field:hero_title" label="Titolo hero" />
              <Input data-testid="guide-hero-title" value={isIt ? (guide.hero_title || "") : tval("field:hero_title")} onChange={(e) => isIt ? set("hero_title", e.target.value) : tset("field:hero_title", e.target.value)} placeholder="Es. Paolo Sarpi 8" />
            </div>
          )}
          {showField("field:hero_subtitle") && (
            <div>
              <StatusRow id="field:hero_subtitle" label="Sottotitolo hero" />
              <Input data-testid="guide-hero-subtitle" value={isIt ? (guide.hero_subtitle || "") : tval("field:hero_subtitle")} onChange={(e) => isIt ? set("hero_subtitle", e.target.value) : tset("field:hero_subtitle", e.target.value)} placeholder="Es. Chinatown · Milano" />
            </div>
          )}
        </div>
        {showField("field:intro") && (<>
          <StatusRow id="field:intro" label="Introduzione / benvenuto" />
          <Textarea data-testid="guide-intro" rows={3} value={isIt ? (guide.intro || "") : tval("field:intro")} onChange={(e) => isIt ? set("intro", e.target.value) : tset("field:intro", e.target.value)}
            placeholder="Benvenuto! Siamo felici di ospitarti…" />
        </>)}
        {isIt && (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {[["show_wifi", "Mostra Wi-Fi"], ["show_checkin", "Mostra check-in/keybox"], ["show_contacts", "Mostra contatti"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-700">
                <Switch checked={!!guide[k]} onCheckedChange={(v) => set(k, v)} data-testid={`guide-${k}`} /> {l}
              </label>
            ))}
          </div>
        )}
      </Card>

      {!isIt && (catalog || []).length > 0 && (
        <Card className="border-slate-200 p-5" data-testid="guide-service-labels">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><ConciergeBell size={16} /> Etichette servizi extra ({elang.toUpperCase()})</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {catalog.filter((c) => showField(`service:${c.key}`)).map((c) => (
              <div key={c.key}>
                <StatusRow id={`service:${c.key}`} label={c.label} />
                <Input value={tval(`service:${c.key}`)} onChange={(e) => tset(`service:${c.key}`, e.target.value)} data-testid={`svc-label-${c.key}`} placeholder={elang === "en" ? c.label_en : c.label_es} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {isIt && (
      <Card className="border-slate-200 p-5" data-testid="guide-gallery-editor">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><ImageIcon size={16} /> Gallery immagini</div>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid="guide-gallery-upload">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Aggiungi foto
            <input type="file" accept="image/*" className="hidden" onChange={uploadGallery} disabled={uploading} />
          </label>
        </div>
        {(guide.gallery || []).length === 0
          ? <p className="text-sm text-slate-400">Nessuna immagine. Aggiungi foto per rendere il Concierge più accogliente.</p>
          : <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
              {(guide.gallery || []).map((g) => (
                <div key={g.id} className="group relative overflow-hidden rounded-xl" data-testid={`guide-gallery-img-${g.id}`}>
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${g.url}`} alt="" className="h-24 w-full object-cover" />
                  <button onClick={() => removeGalleryImg(g.id)} data-testid={`guide-gallery-del-${g.id}`}
                    className="absolute right-1 top-1 rounded-full bg-red-500/90 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>}
      </Card>
      )}

      {isIt && (
      <Card className="border-slate-200 p-5">
        <div className="mb-3 text-sm font-semibold text-slate-700">Contatti assistenza</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input placeholder="Nome referente" value={guide.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} data-testid="guide-contact-name" />
          <Input placeholder="Telefono" value={guide.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} />
          <Input placeholder="WhatsApp (+39…)" value={guide.contact_whatsapp || ""} onChange={(e) => set("contact_whatsapp", e.target.value)} />
          <Input placeholder="Email" value={guide.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} />
        </div>
      </Card>
      )}

      <Card className="border-slate-200 p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-700">Sezioni {!isIt && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs uppercase text-slate-500">{elang}</span>}</div>
          {isIt && <Button size="sm" variant="outline" className="gap-1" onClick={addSection} data-testid="guide-add-section"><Plus size={14} /> Aggiungi</Button>}
        </div>
        <div className="space-y-4">
          {(guide.sections || []).map((s, i) => {
            const tId = `section_title:${s.id}`, bId = `section_body:${s.id}`;
            if (!isIt && !(showField(tId) || showField(bId))) return null;
            return (
              <div key={s.id || i} className="rounded-xl border border-slate-200 p-3" data-testid={`guide-section-${i}`}>
                <div className="mb-2 flex items-center gap-2">
                  <GripVertical size={15} className="text-slate-300" />
                  {isIt ? (
                    <>
                      <Input value={s.title || ""} onChange={(e) => setSection(i, "title", e.target.value)} className="h-8 font-medium" />
                      <Switch checked={s.visible !== false} onCheckedChange={(v) => setSection(i, "visible", v)} />
                      <Button size="icon" variant="ghost" className="text-red-500" onClick={() => removeSection(i)}><Trash2 size={14} /></Button>
                    </>
                  ) : (
                    <div className="flex-1">
                      {showField(tId) && (<><StatusRow id={tId} label={s.title || "Titolo sezione"} /><Input value={tval(tId)} onChange={(e) => tset(tId, e.target.value)} className="h-8 font-medium" placeholder={s.title} /></>)}
                    </div>
                  )}
                </div>
                {isIt ? (
                  <Textarea rows={2} value={s.body || ""} onChange={(e) => setSection(i, "body", e.target.value)} placeholder="Testo della sezione (le sezioni vuote non appaiono nella pagina ospite)" />
                ) : (
                  showField(bId) && (guide.sections[i].body || "").trim() && (<><StatusRow id={bId} label="Testo sezione" /><Textarea rows={2} value={tval(bId)} onChange={(e) => tset(bId, e.target.value)} placeholder={s.body} /></>)
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button data-testid="guide-save-btn" onClick={save}>Salva Welcome Guide</Button>
      </div>
    </div>
  );
}

/* ---------------- Upsell config ---------------- */
function UpsellTab({ aptId }) {
  const [items, setItems] = useState([]);
  const [globalPrices, setGlobalPrices] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/upsell/config?apartment_id=${aptId}`).then((r) => {
      setItems(r.data.items || []);
      setGlobalPrices(r.data.global_prices || {});
    }).catch(() => {});
  }, [aptId]);

  const setItem = (i, k, v) => setItems((arr) => { const a = [...arr]; a[i] = { ...a[i], [k]: v }; return a; });

  const save = async () => {
    try {
      setSaving(true);
      const r = await api.put("/upsell/config", {
        apartment_id: aptId,
        items: items.map((it) => ({ key: it.key, enabled: it.enabled, price: Number(it.price) || 0 })),
        global_prices: globalPrices,
      });
      setItems(r.data.items || []);
      setGlobalPrices(r.data.global_prices || {});
      toast.success("Configurazione upsell salvata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="upsell-editor">
      <Card className="border-slate-200 p-5">
        <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700"><ConciergeBell size={16} /> Servizi extra su richiesta</div>
        <p className="mb-4 text-xs text-slate-500">Attiva i servizi da mostrare nel Concierge. <strong>I prezzi qui sotto sono interni</strong> (riferimento per lo staff) e <strong>NON vengono mostrati all'ospite</strong>: l'ospite invia una richiesta, poi definisci il prezzo nella scheda "Richieste" e invii il link Stripe.</p>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={it.key} data-testid={`upsell-cfg-${it.key}`} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
              <Switch checked={it.enabled} onCheckedChange={(v) => setItem(i, "enabled", v)} data-testid={`upsell-enable-${it.key}`} />
              <span className="flex-1 text-sm font-medium text-slate-800">{it.label}</span>
              <div className="flex items-center gap-1">
                <span className="text-slate-400">€</span>
                <Input type="number" step="1" min="0" value={it.price} data-testid={`upsell-price-${it.key}`}
                  onChange={(e) => setItem(i, "price", e.target.value)} className="h-9 w-24" />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button data-testid="upsell-save-btn" onClick={save} disabled={saving}>Salva upsell</Button>
      </div>
    </div>
  );
}

/* ---------------- Richieste servizi ---------------- */
function RequestsTab({ aptId }) {
  const [meta, setMeta] = useState({ statuses: [] });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/service-requests?apartment_id=${aptId}`).then((r) => setRows(r.data)).catch(() => {});
  }, [aptId]);
  useEffect(() => { api.get("/service-requests/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const statusMeta = (k) => meta.statuses.find((s) => s.key === k) || { label: k, color: "#64748B" };
  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const saveRow = async (r) => {
    try {
      setBusy(r.id);
      const res = await api.put(`/service-requests/${r.id}`, { price: Number(r.price) || 0, status: r.status, staff_notes: r.staff_notes || "" });
      setRow(r.id, res.data);
      toast.success("Richiesta aggiornata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(""); }
  };
  const genLink = async (r) => {
    try {
      setBusy(r.id);
      const res = await api.post(`/service-requests/${r.id}/payment-link`, { origin_url: window.location.origin });
      setRow(r.id, { payment_link: res.data.url, status: "preventivo_inviato" });
      try { await navigator.clipboard?.writeText(res.data.url); } catch (_) { /* clipboard non disponibile */ }
      toast.success("Link di pagamento generato (copiato negli appunti)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(""); }
  };
  const checkPay = async (r) => {
    try {
      setBusy(r.id);
      const res = await api.get(`/service-requests/${r.id}/payment-status`);
      if (res.data.payment_status === "paid") { toast.success("Pagamento ricevuto!"); load(); }
      else toast.message("Pagamento non ancora completato");
    } catch (e) { toast.error("Nessun pagamento collegato"); }
    finally { setBusy(""); }
  };
  const markSent = async (r, channel) => {
    try {
      const res = await api.post(`/service-requests/${r.id}/mark-sent`, { channel });
      setRow(r.id, res.data);
    } catch (_) { /* tracking best-effort */ }
  };
  const preventivoMsg = (r) => {
    const price = Number(r.price) || 0;
    const who = r.guest_name ? `Ciao ${r.guest_name}, ` : "Ciao, ";
    return `${who}ecco il preventivo per "${r.item_label}"${r.guests ? ` (${r.guests} persone)` : ""}: € ${price.toFixed(2)}. ` +
      `Puoi confermare e pagare in sicurezza a questo link: ${r.payment_link}\n\nUrban Dreams Home`;
  };
  const waHref = (r) => {
    const digits = (r.guest_contact || "").replace(/[^0-9]/g, "");
    const base = digits.length >= 8 ? `https://wa.me/${digits}` : "https://wa.me/";
    return `${base}?text=${encodeURIComponent(preventivoMsg(r))}`;
  };
  const mailHref = (r) => {
    const to = (r.guest_contact || "").includes("@") ? r.guest_contact : "";
    return `mailto:${to}?subject=${encodeURIComponent(`Preventivo servizio · ${r.item_label}`)}&body=${encodeURIComponent(preventivoMsg(r))}`;
  };

  return (
    <div className="space-y-4" data-testid="requests-editor">
      <div className="flex items-center gap-2 text-sm text-slate-500"><Inbox size={16} /> Richieste servizi ricevute dagli ospiti · definisci il prezzo e invia il link di pagamento.</div>
      {rows.length === 0 && <Card className="p-8 text-center text-sm text-slate-400" data-testid="requests-empty">Nessuna richiesta ricevuta per questo immobile.</Card>}
      <div className="space-y-3">
        {rows.map((r) => {
          const sm = statusMeta(r.status);
          return (
            <Card key={r.id} className="border-slate-200 p-4" data-testid={`request-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800">{r.item_label}</span>
                    <Badge style={{ backgroundColor: sm.color + "22", color: sm.color }} className="border-0">{sm.label}</Badge>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">{r.guests} persone{r.guest_name ? ` · ${r.guest_name}` : ""}{r.guest_contact ? ` · ${r.guest_contact}` : ""} · {(r.created_at || "").slice(0, 10)}</div>
                  {r.note && <div className="mt-1 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600">{r.note}</div>}
                </div>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-500">Prezzo (€)</label>
                  <Input type="number" step="1" min="0" value={r.price || 0} data-testid={`request-price-${r.id}`}
                    onChange={(e) => setRow(r.id, { price: e.target.value })} className="h-9" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-500">Stato</label>
                  <Select value={r.status} onValueChange={(v) => setRow(r.id, { status: v })}>
                    <SelectTrigger className="h-9" data-testid={`request-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.statuses.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-500">Note staff</label>
                  <Input value={r.staff_notes || ""} onChange={(e) => setRow(r.id, { staff_notes: e.target.value })} className="h-9" placeholder="Note interne" />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => saveRow(r)} disabled={busy === r.id} data-testid={`request-save-${r.id}`}>Salva</Button>
                <Button size="sm" variant="outline" className="gap-1" onClick={() => genLink(r)} disabled={busy === r.id} data-testid={`request-genlink-${r.id}`}>
                  {busy === r.id ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />} Genera link Stripe
                </Button>
                {r.payment_link && (
                  <>
                    <a href={waHref(r)} target="_blank" rel="noreferrer" onClick={() => markSent(r, "whatsapp")} data-testid={`request-send-wa-${r.id}`}
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-600"><MessageCircle size={13} /> Invia preventivo · WhatsApp</a>
                    <a href={mailHref(r)} onClick={() => markSent(r, "email")} data-testid={`request-send-email-${r.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"><Mail size={13} /> Email</a>
                    <Button size="sm" variant="outline" className="gap-1" onClick={async () => { try { await navigator.clipboard?.writeText(r.payment_link); toast.success("Link copiato"); } catch (_) { toast.message("Copia il link manualmente"); } }} data-testid={`request-copy-${r.id}`}><Copy size={13} /> Copia link</Button>
                    <Button size="sm" variant="ghost" className="gap-1" onClick={() => checkPay(r)} disabled={busy === r.id} data-testid={`request-checkpay-${r.id}`}><RefreshCw size={13} /> Verifica pagamento</Button>
                    {r.last_sent_at && <span className="text-[11px] text-slate-400">Inviato via {r.last_sent_channel} · {(r.last_sent_at || "").slice(0, 10)}</span>}
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Analytics ---------------- */
function AnalyticsTab({ aptId }) {
  const [data, setData] = useState(null);
  const [codes, setCodes] = useState([]);

  useEffect(() => {
    api.get(`/qr/analytics?apartment_id=${aptId}&days=30`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/qr-codes?apartment_id=${aptId}`).then((r) => setCodes(r.data)).catch(() => {});
  }, [aptId]);

  const codeName = useMemo(() => Object.fromEntries(codes.map((c) => [c.id, c.title])), [codes]);
  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Caricamento…</p>;

  const kpis = [
    { icon: ScanLine, label: "Scansioni (30gg)", value: data.total_scans, accent: "#2563EB" },
    { icon: Users, label: "Visitatori unici", value: data.unique_visitors, accent: "#7C3AED" },
    { icon: TrendingUp, label: "Conversioni", value: data.conversions, sub: `${data.conversion_rate ?? 0}% sulle scansioni`, accent: "#059669" },
    { icon: Euro, label: "Ricavi upsell", value: `€${(data.revenue || 0).toFixed(0)}`, sub: "servizi extra pagati", accent: "#D97706" },
  ];

  return (
    <div className="space-y-5" data-testid="qr-analytics">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {kpis.map((k) => (
          <Card key={k.label} className="p-5">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
              <k.icon size={15} style={{ color: k.accent }} /> {k.label}
            </div>
            <div className="mt-2 font-head text-3xl font-semibold text-slate-900">{k.value}</div>
            {k.sub && <div className="mt-1 text-[11px] text-slate-400">{k.sub}</div>}
          </Card>
        ))}
      </div>

      <Card className="border-slate-200 p-5">
        <div className="mb-3 text-sm font-semibold text-slate-700">Scansioni ultimi 30 giorni</div>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={data.series}>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
              <Tooltip />
              <Bar dataKey="scans" fill="#4F46E5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="border-slate-200 p-5">
        <div className="mb-3 text-sm font-semibold text-slate-700">Scansioni per QR</div>
        {Object.keys(data.by_qr).length === 0 && <p className="text-sm text-slate-400">Nessuna scansione registrata.</p>}
        <div className="space-y-2">
          {Object.entries(data.by_qr).map(([qid, n]) => (
            <div key={qid} className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{codeName[qid] || qid.slice(0, 8)}</span>
              <Badge variant="secondary">{n}</Badge>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
